<script setup lang="ts">
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { computed, ref } from 'vue'
import { useForemanApiKeys } from '@/composables/core/foreman-api-keys'
import { useForemanClientsToSiteMappings } from '@/composables/core/foreman-site-mappings'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import RlEmptyDataset from '@/components/common/RlEmptyDataset.vue'
import ForemanAPIKeyCreationDialog from '@/components/core/foreman-configuration/ForemanAPIKeyCreationDialog.vue'
import SiteMappingCreationDialog from '@/components/core/foreman-configuration/SiteMappingCreationDialog.vue'

const foremanAPIKeyCreationDialogVisible = ref<boolean>(false)
const mappingCreationDialogVisible = ref<boolean>(false)

const { getMappingsQuery, deleteMappingMutation } = useForemanClientsToSiteMappings()
const { getApiKeyQuery, deleteApiKeyMutation } = useForemanApiKeys()

const {
  isPending: mappingsIsPending,
  isError: mappingsIsError,
  isRefetching: mappingsIsRefetching,
  data: mappingsData,
  error: mappingsError,
  refetch: mappingRefetch,
} = getMappingsQuery()
const {
  isPending: apiKeyIsPending,
  isError: apiKeyIsError,
  isRefetching: apiKeyIsRefetching,
  data: apiKeyData,
  error: apiKeyError,
  refetch: apiKeyRefetch,
} = getApiKeyQuery()

const anyError = computed(() => {
  return (mappingsIsError && !mappingsIsRefetching) || (apiKeyIsError && !apiKeyIsRefetching)
})
const error = computed(() => {
  if (mappingsError) return mappingsError
  if (apiKeyError) return apiKeyError
  return null
})
const refetchData = () => {
  mappingRefetch()
  apiKeyRefetch()
}

const onApiKeyDelete = (apiKeyID: number) => {
  DeleteConfirmationModal(
    'Delete API key',
    `Are you sure you want to delete your Foreman API key? All existing mappings will be deleted as well`,
    apiKeyID,
    deleteApiKeyMutation.mutateAsync
  )
}
</script>

<template>
  <ViewLayout>
    <template #controls>
      <el-button
        v-if="apiKeyData"
        type="primary"
        :disabled="apiKeyIsPending"
        @click="
          () => {
            mappingCreationDialogVisible = true
          }
        "
        >Add Site Mapping</el-button
      >
      <el-button v-if="apiKeyData" type="danger" :disabled="apiKeyIsPending" @click="onApiKeyDelete(apiKeyData.id)"
        >Delete API key</el-button
      >
    </template>
    <rl-error-result v-if="anyError" :error="error">
      <el-button @click="() => refetchData()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <template v-if="apiKeyData">
        <el-table v-loading="mappingsIsPending" :data="mappingsData" empty-text="No site mappings" stripe>
          <el-table-column prop="site.name" label="Site" :min-width="160" />
          <el-table-column label="Foreman Client IDs" :min-width="160">
            <template #default="scope">
              <ul v-if="scope.row.foreman_client_ids.length > 0">
                <li v-for="(item, index) in scope.row.foreman_client_ids" :key="index">{{ item }}</li>
              </ul>
            </template>
          </el-table-column>
          <el-table-column label="Foreman Client Names" :min-width="160">
            <template #default="scope">
              <ul v-if="scope.row.foreman_client_names.length > 0">
                <li v-for="(item, index) in scope.row.foreman_client_names" :key="index">{{ item }}</li>
              </ul>
            </template>
          </el-table-column>
          <el-table-column :min-width="70" align="right">
            <template #default="scope">
              <el-button
                link
                title="Delete"
                @click="
                  DeleteConfirmationModal(
                    'Delete Mapping',
                    'Are you sure you want to delete this mapping? This action cannot be undone',
                    scope.row.id,
                    deleteMappingMutation.mutateAsync
                  )
                "
              >
                <Icon icon="material-symbols:delete-outline-rounded" inline height="20" />
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </template>
      <template v-else>
        <RlEmptyDataset
          title="Foreman API key not found"
          subtitle="Submit an API key to start configuring your Foreman integration"
        >
          <el-button
            type="primary"
            @click="
              () => {
                foremanAPIKeyCreationDialogVisible = true
              }
            "
            >Submit API key</el-button
          >
        </RlEmptyDataset>
      </template>
    </template>
    <ForemanAPIKeyCreationDialog v-model="foremanAPIKeyCreationDialogVisible" />
    <SiteMappingCreationDialog v-model="mappingCreationDialogVisible" />
  </ViewLayout>
</template>
